import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import OrderStatusBadge from '@/components/Orders/OrderStatusBadge/OrderStatusBadge';
import { ORDER_ITEM_STATUS_LABELS, type Order } from '@/types/order';
import styles from './OrderDetailPage.module.scss';

const POLL_MS = 10000;

function eventText(eventType: string): string {
  const map: Record<string, string> = {
    'order.created': 'Order created',
    'order.submitted': 'Submitted to branch',
    'order.updated': 'Items reviewed by branch',
    'order.delivered': 'Marked delivered',
    'order.closed': 'Order closed',
    'order.cancelled': 'Order cancelled',
    'order.comment': 'Comment added',
  };
  return map[eventType] || eventType.replace(/[._]/g, ' ');
}

export default function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [commentSent, setCommentSent] = useState(false);
  const mounted = useRef(true);

  const load = useCallback(async (showSpinner = false) => {
    if (!orderId) return;
    if (showSpinner) setLoading(true);
    try {
      const o = await apiService.getOrder(orderId);
      if (!mounted.current) return;
      setOrder(o);
      setError(null);
    } catch (e: any) {
      if (mounted.current) setError(e?.message || 'Failed to load order.');
    } finally {
      if (mounted.current && showSpinner) setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    mounted.current = true;
    load(true);
    const id = setInterval(() => load(false), POLL_MS);
    return () => {
      mounted.current = false;
      clearInterval(id);
    };
  }, [load]);

  const runAction = async (fn: () => Promise<Order>) => {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await fn();
      if (mounted.current) setOrder(updated);
    } catch (e: any) {
      if (mounted.current) setActionError(e?.message || 'Action failed.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const handleShare = async () => {
    if (!order) return;
    setBusy(true);
    setActionError(null);
    try {
      const thread = await apiService.createChatThread(order.branchId);
      await apiService.postChatMessage(thread.id, { type: 'ORDER_REF', orderId: order.id });
      navigate(`/customer/branches/${order.branchId}/chat?thread=${thread.id}`);
    } catch (e: any) {
      if (mounted.current) setActionError(e?.message || 'Could not share order to chat.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const handleComment = async () => {
    if (!order || !comment.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      await apiService.commentOnOrder(order.id, comment.trim());
      setComment('');
      setCommentSent(true);
      await load(false);
    } catch (e: any) {
      if (mounted.current) setActionError(e?.message || 'Could not post comment.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  if (loading) {
    return (
      <>
        <DashboardHeader />
        <div className={styles.container}><p className={styles.muted}>Loading order…</p></div>
        <Footer />
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <DashboardHeader />
        <div className={styles.container}>
          <button type="button" className={styles.backButton} onClick={() => navigate('/customer/orders')}>← My orders</button>
          <p className={styles.error}>{error || 'Order not found.'}</p>
        </div>
        <Footer />
      </>
    );
  }

  const canSubmit = order.status === 'CREATED';
  const canCancel = !['DELIVERED', 'CLOSED', 'CANCELLED'].includes(order.status);
  const canComment = order.status === 'DELIVERED' || order.status === 'CLOSED';
  const events = [...(order.events ?? [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <button type="button" className={styles.backButton} onClick={() => navigate('/customer/orders')}>← My orders</button>

        <div className={styles.headerRow}>
          <h1 className={styles.title}>Order #{order.orderNumber}</h1>
          <OrderStatusBadge status={order.status} />
        </div>
        {order.note && <p className={styles.note}>“{order.note}”</p>}

        <div className={styles.actions}>
          {canSubmit && (
            <button type="button" className={styles.primaryBtn} disabled={busy}
              onClick={() => runAction(() => apiService.submitOrder(order.id))}>
              Submit to branch
            </button>
          )}
          <button type="button" className={styles.secondaryBtn} disabled={busy} onClick={handleShare}>
            Share in chat
          </button>
          {canCancel && (
            <button type="button" className={styles.dangerBtn} disabled={busy}
              onClick={() => runAction(() => apiService.cancelOrder(order.id))}>
              Cancel order
            </button>
          )}
        </div>
        {actionError && <p className={styles.error}>{actionError}</p>}

        <h2 className={styles.sectionTitle}>Items</h2>
        <ul className={styles.items}>
          {order.items.map((it) => (
            <li key={it.id} className={styles.item} data-status={it.status}>
              <div className={styles.itemMain}>
                <span className={styles.itemName}>{it.name}</span>
                <span className={styles.itemQty}>{it.quantity}{it.unit ? ` ${it.unit}` : ''}</span>
              </div>
              <div className={styles.itemMeta}>
                <span className={styles.itemStatus} data-status={it.status}>
                  {ORDER_ITEM_STATUS_LABELS[it.status] ?? it.status}
                </span>
                {it.organizerNote && <span className={styles.organizerNote}>Branch: {it.organizerNote}</span>}
              </div>
            </li>
          ))}
        </ul>

        {canComment && (
          <div className={styles.commentBox}>
            <h2 className={styles.sectionTitle}>Leave a comment</h2>
            <p className={styles.muted}>Your comment is shared with the branch in chat.</p>
            <textarea
              className={styles.commentInput}
              rows={3}
              placeholder="How was your order?"
              value={comment}
              onChange={(e) => { setComment(e.target.value); setCommentSent(false); }}
            />
            <div className={styles.commentActions}>
              {commentSent && <span className={styles.sentNote}>Comment posted ✓</span>}
              <button type="button" className={styles.primaryBtn} disabled={busy || !comment.trim()} onClick={handleComment}>
                Post comment
              </button>
            </div>
          </div>
        )}

        <h2 className={styles.sectionTitle}>Timeline</h2>
        {events.length === 0 ? (
          <p className={styles.muted}>No activity yet.</p>
        ) : (
          <ul className={styles.timeline}>
            {events.map((ev) => (
              <li key={ev.id} className={styles.timelineItem}>
                <span className={styles.dot} aria-hidden />
                <div>
                  <span className={styles.eventType}>{eventText(ev.eventType)}</span>
                  <span className={styles.eventDate}>
                    {new Date(ev.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Footer />
    </>
  );
}
